<template>
  <div class="patient-info">
    <b-row>
      <b-col class="form-box">
        <div class="structure">
          <span class="structure__heading">
            <i
              class="far fa-user mr-2"
              aria-hidden="true"
            />{{ $t('global.personalData') }}
          </span>
          <b-row class="mt-2">
            <b-col
              v-for="patientIdentifier in patient.patientIdentifiers"
              :key="patientIdentifier.paiId"
              md="6"
              lg="4"
              class="custom-width"
            >
              <div class="structure__data">
                <small>{{ patientIdentifier.identifierType.name }} <i
                  v-if="patientIdentifier.identifierType.code === PATIENT_IDENTIFIER_CODES.NHC"
                  v-tooltip="patientIdentifier.center.name"
                  class="ml-1 far fa-clinic-medical"
                  aria-hidden="true"
                /></small>
                <h4
                  v-testing="`identifier-${patientIdentifier.identifierType.idtId}`"
                  class="collapsable-card-title inside"
                  style="word-break: break-word"
                >
                  {{ patientIdentifier.paiIdentifier }}
                </h4>
              </div>
            </b-col>
          </b-row>
          <div class="horizontal-divider my-3" />
          <b-row>
            <b-col
              md="6"
            >
              <div class="structure__data">
                <small>{{ $tc('global.name',1) }}</small>
                <h4
                  v-testing="'name'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.patName || patient.name }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="globalParameters.middleNameSupported"
              md="6"
            >
              <div class="structure__data">
                <small>{{ $t('global.middleName') }}</small>
                <h4
                  v-testing="'middlename'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.patMiddleName || patient.middleName }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="6"
            >
              <div class="structure__data">
                <small>{{ $t('global.surname1') }}</small>
                <h4
                  v-testing="'surname1'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.patSurname1 || patient.surname1 }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="globalParameters.secondSurnameSupported"
              md="6"
            >
              <div class="structure__data">
                <small>{{ $t('global.surname2') }}</small>
                <h4
                  v-testing="'surname2'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.patSurname2 || patient.surname2 }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="6"
            >
              <div class="structure__data">
                <small>{{ $t('global.birthday') }}</small>
                <h4
                  v-testing="'birthdate'"
                  class="collapsable-card-title inside"
                >
                  {{ patient.patBirthdate || patient.birthdate | date }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="6"
            >
              <div class="structure__data">
                <small>{{ $tc('global.gender') }}</small>
                <h4
                  v-testing="'gender'"
                  class="collapsable-card-title inside"
                >
                  {{ patient.genderName || patient.gender.name }}
                </h4>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="form-box">
        <div class="structure">
          <span class="structure__heading">
            <i
              class="far fa-envelope mr-2"
              aria-hidden="true"
            />{{ $t('global.contactData') }}
          </span>

          <b-row class="mt-2">
            <b-col
              md="8"
            >
              <div class="structure__data">
                <small>{{ $tc('global.address',1) }}</small>
                <h4
                  v-testing="'address'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.address && patient.address.address ? patient.address.address : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="4"
            >
              <div class="structure__data">
                <small>{{ $t('global.pc') }}</small>
                <h4
                  v-testing="'postal-code'"
                  class="collapsable-card-title inside"
                >
                  {{ patient.address && patient.address.postalCode ? patient.address.postalCode : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="4"
            >
              <div class="structure__data">
                <small>{{ $tc('global.municipality',1) }}</small>
                <h4
                  v-testing="'municipality'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.address && patient.address.municipality ? patient.address.municipality.name : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="4"
            >
              <div class="structure__data">
                <small>{{ $tc('global.province',1) }}</small>
                <h4
                  v-testing="'province'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.address && patient.address.province ? patient.address.province.name : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="4"
            >
              <div class="structure__data">
                <small>{{ $tc('global.country',1) }}</small>
                <h4
                  v-testing="'country'"
                  class="collapsable-card-title inside is-truncate"
                >
                  {{ patient.address && patient.address.country ? patient.address.country.name : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="4"
            >
              <div class="structure__data">
                <small>{{ $tc('global.phone',1) }} 1</small>
                <h4
                  v-testing="'phone1'"
                  class="collapsable-card-title inside"
                >
                  {{ patient.contactInformation && patient.contactInformation.mainPhone ? patient.contactInformation.mainPhone : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="4"
            >
              <div class="structure__data">
                <small>{{ $tc('global.phone',1) }} 2</small>
                <h4
                  v-testing="'phone2'"
                  class="collapsable-card-title inside"
                >
                  {{ patient.contactInformation && patient.contactInformation.otherPhone1 ? patient.contactInformation.otherPhone1 : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="4"
            >
              <div class="structure__data">
                <small>{{ $tc('global.phone',1) }} 3</small>
                <h4
                  v-testing="'phone3'"
                  class="collapsable-card-title inside"
                >
                  {{ patient.contactInformation && patient.contactInformation.otherPhone2 ? patient.contactInformation.otherPhone2 : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              md="12"
            >
              <div class="structure__data">
                <small>{{ $tc('global.email',1) }}</small>
                <h4
                  v-testing="'email'"
                  class="collapsable-card-title inside"
                >
                  {{ patient.contactInformation && patient.contactInformation.email ? patient.contactInformation.email : '-' }}
                </h4>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="form-box">
        <div class="structure">
          <span class="structure__heading">
            <i
              class="far fa-comment-lines mr-2"
              aria-hidden="true"
            />{{ $t('global.observations') }}
          </span>
          <b-row class="d-flex pl-3 pr-3">
            <div class="col pl-0 pr-0">
              <h4
                v-testing="'observations'"
                class="collapsable-card-title inside"
              >
                {{ patient.notes && patient.notes.text ? patient.notes.text : $t('global.noObservations') }}
              </h4>
            </div>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import { PATIENT_IDENTIFIER_CODES } from '@/api/resources/IdentifierRegexesService'

export default {
    name: 'PatientInfo',
    props: {
        patient: { type: Object, required: true }
    },
    data() {
        return {
            PATIENT_IDENTIFIER_CODES: PATIENT_IDENTIFIER_CODES
        }
    }
}

</script>
